"use client";

import React, { useRef } from "react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import Typography from "./Typography";

export interface Airline {
  code: string;
  logo: string;
  name: string;
}

interface AirlinesSliderProps {
  title: string;
  subtitle: string;
  airlines: Airline[];
}

const AirlinesSlider = ({ title, subtitle, airlines }: AirlinesSliderProps) => {
  const swiperRef = useRef<SwiperInstance | null>(null);

  if (!airlines.length) return null;

  return (
    <section className="mb-7 lg:mb-10">
      <div className="mb-4">
        <Typography variant="h2" className="text-lg font-bold md:text-xl">
          {title}
        </Typography>
        <Typography
          variant="body2"
          className="mt-1 block text-sm text-foreground/70"
        >
          {subtitle}
        </Typography>
      </div>
      <div
        onMouseEnter={() => swiperRef.current?.autoplay.pause()}
        onMouseLeave={() => swiperRef.current?.autoplay.resume()}
      >
        <Swiper
          modules={[Autoplay]}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          loop={airlines.length >= 9}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
          }}
          speed={6000}
          slidesPerView={2}
          spaceBetween={12}
          className="[&_.swiper-wrapper]:!ease-linear"
          breakpoints={{
            768: { slidesPerView: 4, spaceBetween: 10 },
            1024: { slidesPerView: 6, spaceBetween: 12 },
            1440: { slidesPerView: 8, spaceBetween: 12 },
          }}
        >
          {airlines.map((airline) => (
            <SwiperSlide key={airline.code}>
              <article className="flex h-32 flex-col items-center justify-center gap-3 rounded-sm border border-border bg-background p-4">
                <img
                  src={airline.logo}
                  alt={airline.name}
                  width={64}
                  height={64}
                  loading="lazy"
                  decoding="async"
                  className="h-14 w-14 object-contain"
                />
                <Typography
                  variant="body2"
                  className="w-full truncate text-center text-sm font-semibold"
                >
                  {airline.name}
                </Typography>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default AirlinesSlider;
